import { Tag, Tooltip } from 'antd';
import React from 'react';
import { getOrderDynamicTagTooltipLine, getUserTagTooltipTitle } from '../common/utils';

const TAG_THEME_PALETTE = [
  { bg: '#f9f9f9', border: '#d9d9d9', text: '#434343' },
  { bg: '#fff7e6', border: '#ffd591', text: '#ad6800' },
  { bg: '#f6ffed', border: '#b7eb8f', text: '#389e0d' },
  { bg: '#fff1f0', border: '#ffa39e', text: '#cf1322' },
  { bg: '#f0f5ff', border: '#adc6ff', text: '#1d39c4' },
  { bg: '#f9f0ff', border: '#d3adf7', text: '#722ed1' },
];
const MAX_VISIBLE_TAGS = 3;
const MAX_VISIBLE_TAGS_MOBILE = 5;
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

const getThemeIndex = (seed = '') => {
  if (!seed) return 0;
  return (
    seed?.split('')?.reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    TAG_THEME_PALETTE?.length
  );
};

const tagMarginClass = (index, { noLeadingMargin }) => {
  if (index === 0 && noLeadingMargin) return 'mr-0';
  return 'ml-8 mr-0';
};

const preventCardNavigation = (event) => {
  event?.preventDefault?.();
  event?.stopPropagation?.();
};

const OrderDynamicTags = ({
  tags = [],
  noLeadingMargin = false,
  getTooltipTitle = getUserTagTooltipTitle,
  getOverflowTooltipLine = getOrderDynamicTagTooltipLine,
}) => {
  const [openTooltipKey, setOpenTooltipKey] = React.useState(null);
  const [isMobileView, setIsMobileView] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const updateMobileView = (event) => setIsMobileView(event.matches);
    setIsMobileView(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', updateMobileView);
      return () => mediaQuery.removeEventListener('change', updateMobileView);
    }

    mediaQuery.addListener(updateMobileView);
    return () => mediaQuery.removeListener(updateMobileView);
  }, []);

  React.useEffect(() => {
    if (!openTooltipKey) return undefined;

    const closeTooltip = () => setOpenTooltipKey(null);
    const handleOutsidePointerDown = (event) => {
      const target = event?.target;
      if (!target?.closest) {
        closeTooltip();
        return;
      }

      if (target.closest('.order-dynamic-tag') || target.closest('.ant-tooltip')) return;
      closeTooltip();
    };

    window.addEventListener('scroll', closeTooltip, true);
    window.addEventListener('wheel', closeTooltip, true);
    window.addEventListener('touchmove', closeTooltip, true);
    document.addEventListener('pointerdown', handleOutsidePointerDown, true);

    return () => {
      window.removeEventListener('scroll', closeTooltip, true);
      window.removeEventListener('wheel', closeTooltip, true);
      window.removeEventListener('touchmove', closeTooltip, true);
      document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
    };
  }, [openTooltipKey]);

  if (!Array.isArray(tags) || tags.length === 0) return null;

  const handleTagClick = (event, tooltipKey) => {
    preventCardNavigation(event);
    setOpenTooltipKey((previousKey) => (previousKey === tooltipKey ? null : tooltipKey));
  };

  const handleTooltipVisibility = (tooltipKey, isOpen) => {
    setOpenTooltipKey(isOpen ? tooltipKey : null);
  };

  const normalizedTags = tags.map((raw) => {
    const label = raw?.value || '-';
    const title = raw?.itemKey || raw?.item_key || label;
    const key = raw?.id || `${title}-${label}`;
    return { key, label, title, id: raw?.id, raw };
  });

  const maxVisibleTags = isMobileView ? MAX_VISIBLE_TAGS_MOBILE : MAX_VISIBLE_TAGS;
  const visibleTags = normalizedTags.slice(0, maxVisibleTags);
  const hiddenTags = normalizedTags.slice(maxVisibleTags);
  const hiddenCount = hiddenTags.length;

  return (
    <>
      {visibleTags.map((tag, index) => {
        const { key, label, title, raw } = tag;
        const theme =
          TAG_THEME_PALETTE[getThemeIndex(`${tag?.id || ''}${title}${label}`)];

        return (
          <Tooltip
            key={key}
            title={getTooltipTitle(raw)}
            placement="top"
            open={openTooltipKey === key}
            onOpenChange={(isOpen) => handleTooltipVisibility(key, isOpen)}
            trigger={['hover']}
            zIndex={4}
          >
            <Tag
              className={`order-dynamic-tag ${tagMarginClass(index, { noLeadingMargin })}`}
              onClick={(event) => handleTagClick(event, key)}
              onTouchStart={preventCardNavigation}
              style={{
                backgroundColor: theme?.bg,
                borderColor: theme?.border,
                color: theme?.text,
              }}
            >
              {label}
            </Tag>
          </Tooltip>
        );
      })}
      {hiddenCount > 0 && (
        <Tooltip
          title={
            <div className="order-dynamic-tags-tooltip-list">
              {hiddenTags.map((tag) => (
                <div key={tag.key} className="order-dynamic-tags-tooltip-line">
                  {getOverflowTooltipLine(tag.raw)}
                </div>
              ))}
            </div>
          }
          placement="top"
          open={openTooltipKey === 'overflow-tags'}
          onOpenChange={(isOpen) => handleTooltipVisibility('overflow-tags', isOpen)}
          trigger={['hover']}
          zIndex={4}
        >
          <Tag
            className={`order-dynamic-tag order-dynamic-tag-overflow ${tagMarginClass(visibleTags.length, { noLeadingMargin })}`}
            onClick={(event) => handleTagClick(event, 'overflow-tags')}
            onTouchStart={preventCardNavigation}
          >
            +{hiddenCount}
          </Tag>
        </Tooltip>
      )}
    </>
  );
};

export default OrderDynamicTags;
